import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

import { usePermission } from "../../../features/auth/use-permission";
import { FirstZoneFields } from "../../../features/facility-profile/components/first-zone-fields";
import { offeredZone } from "../../../features/facility-profile/time-zones";
import {
  onboardingQueryOptions,
  type OnboardingFacility,
} from "../../../features/onboarding/api/onboarding";
import { OnboardingAdmin } from "../../../features/onboarding/components/onboarding-admin";

/**
 * `RPM-061`'s onboarding checklist (`J-295`): every building of the tenant with what it still needs,
 * and Ready yes or no. A building with no profile is opened here and given its first time zone
 * (`CHUNK-8.md` §3 step 6); back on the list it shows its next gap.
 *
 * THE ROUTE COMPOSES TWO FEATURES, as `.claude/rules/coding.md` asks: the list is `onboarding`'s and
 * the zone's fields and confirmation are `facility-profile`'s. Giving a zone asks
 * `facilityProfile: ["create"]`, as the server does; an account without it reads the list and is told
 * in words that it may not set one.
 */
function OnboardingScreen() {
  const client = useQueryClient();
  const canSetZone = usePermission({ facilityProfile: ["create"] }, ["TENANT"]);
  const [opened, setOpened] = useState<OnboardingFacility | null>(null);
  const [zone, setZone] = useState("");
  const [invalid, setInvalid] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const close = () => {
    setOpened(null);
    setConfirming(false);
    setInvalid(false);
  };

  return (
    <OnboardingAdmin
      canSetZone={canSetZone}
      announcement={announcement}
      onOpen={(building) => {
        if (!canSetZone) {
          setAnnouncement(
            `You may read ${building.name}'s setup, but you do not have permission to give it a time zone.`,
          );
          return;
        }
        setOpened(building);
        setZone(offeredZone(building.mirrorZone));
        setInvalid(false);
        setConfirming(false);
        setAnnouncement("");
      }}
      {...(opened === null
        ? {}
        : {
            form: {
              heading: `Give ${opened.name} its time zone`,
              submitLabel: zone === "" ? "Set the zone" : `Set the zone to ${zone}`,
              onSubmit: () => {
                if (zone === "") {
                  setInvalid(true);
                  return;
                }
                setConfirming(true);
              },
              onCancel: close,
              children: (
                <FirstZoneFields
                  key={opened.facilityId}
                  facilityId={opened.facilityId}
                  mirrorZone={opened.mirrorZone}
                  zone={zone}
                  onZoneChange={(next) => {
                    setZone(next);
                    setInvalid(false);
                  }}
                  invalid={invalid}
                  confirming={confirming}
                  onCloseConfirmation={() => setConfirming(false)}
                  onSet={(created) => {
                    setAnnouncement(
                      `${opened.name} now has the time zone ${created.profile.ianaTimeZone}. The list shows what it needs next.`,
                    );
                    close();
                    // The list, readiness and the pick list all read the profile this wrote.
                    void client.invalidateQueries({ queryKey: onboardingQueryOptions().queryKey });
                    void client.invalidateQueries({ queryKey: ["readiness"] });
                    void client.invalidateQueries({ queryKey: ["facility-pick"] });
                  }}
                />
              ),
            },
          })}
    />
  );
}

export const Route = createFileRoute("/_authenticated/admin/onboarding")({
  component: OnboardingScreen,
  staticData: { screenName: "Onboarding" },
});
